// ============================================================================
// Service Desk — frontend service layer.
// Talks to the torre-de-control-api serviceDesk module. Two flavors:
//   • authed calls go through the shared `api` client (token + session headers)
//   • public calls (anonymous portal) go through a bare axios instance so the
//     session-expiry interceptor never kicks in for logged-out visitors.
//
// `unwrap` (./envelope) reaches the real payload through the platform's
// double envelope ({ meta, data: { message, data } }); endpoints that use
// `pageResponse` (listTickets) are single-level and handled inline.
// ============================================================================
import axios from "axios";
import api, { apiFormData } from "../../../utils/api";
import { unwrap } from "./envelope";
import { sampleViewHeaders, readSandboxView, useSampleViewStore, type SandboxView } from "./sampleView";

// Vista de ejemplo (opt-in): sólo las peticiones a la mesa llevan su encabezado.
// (Las pruebas sustituyen el cliente por uno sin interceptores.)
for (const client of [api, apiFormData]) {
  client?.interceptors?.request?.use?.((config) => {
    const url = String(config.url || "");
    if (url.startsWith("/serviceDesk")) {
      const extra = sampleViewHeaders();
      if (Object.keys(extra).length) {
        config.headers = config.headers || {};
        for (const [k, v] of Object.entries(extra)) (config.headers as Record<string, string>)[k] = v;
      }
    }
    return config;
  });
}

const API_URL =
  import.meta.env.VITE_FIREBASE_API_URL ||
  "http://127.0.0.1:5001/cc-console-development/us-central1/wayakDevelopmentApi";

const publicApi = axios.create({ baseURL: API_URL, timeout: 30000 });

// `unwrap` (./envelope) reaches the real payload through the platform's
// `{ meta, data }` + the serviceDesk controller's `{ message, data }` wrapping.
// Endpoints that use `pageResponse` (listTickets) are single-level and are
// handled inline below.

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export type TicketPriority = "low" | "medium" | "high" | "urgent";
export type SlaState = "met" | "on_time" | "at_risk" | "breached" | "unknown";

export interface FirestoreTimestamp {
  _seconds?: number;
  seconds?: number;
  _nanoseconds?: number;
  nanoseconds?: number;
}
export type TimestampLike = FirestoreTimestamp | string | number | null;

export interface BrandOption { id: string; label: string }
export interface CategoryOption { id: string; label: string }
export interface LabelOption { id: string; label: string; color?: string }
export interface StatusOption { id: string; label: string; color?: string; terminal?: boolean; pausesSla?: boolean }

export interface SlaTarget { firstResponseMins: number; resolutionMins: number }

// Look & feel for the agent workspace + public portal. All optional; empty
// values fall back to the tenant theme / built-in copy.
export interface Appearance {
  primaryColor?: string;
  headerTitle?: string;
  headerSubtitle?: string;
  logoUrl?: string;
  portalIntro?: string;
}

/**
 * Etapas del ciclo de desarrollo (#559). Los ids son FIJOS —la API máquina, las
 * transiciones automáticas y la vista del cliente dependen de ellos—; lo que la
 * mesa configura es el nombre, el color y si usa o no cada etapa.
 * Las siete primeras son el carril lineal; las dos últimas son laterales:
 * interrumpen el carril sin perder el sitio.
 */
export type DevStageId =
  | "received"
  | "analysis"
  | "development"
  | "pr_review"
  | "qa_deploy"
  | "staff_validated"
  | "resolved"
  | "waiting_customer"
  | "qa_rejected";

export interface StageOption {
  id: DevStageId;
  label: string;
  color: string;
  kind: "linear" | "side";
  terminal?: boolean;
  enabled?: boolean;
}

/** Un peldaño de la línea de tiempo tal como lo pinta la UI. */
export interface StageStep {
  id: DevStageId;
  label: string;
  color: string;
  kind?: "linear" | "side";
  /** Epoch ms en que se alcanzó, o null si todavía no. */
  at: number | null;
  status: "done" | "current" | "pending";
}

/** Enlaces técnicos: SOLO llegan en la proyección interna. */
export interface DevStageLinks {
  issueUrl?: string | null;
  prUrl?: string | null;
  qaUrl?: string | null;
  branch?: string | null;
  repo?: string | null;
}

export interface DevStageHistoryEntry {
  stage: DevStageId;
  label: string;
  at: number | null;
  source: "system" | "agent" | "staff";
  actorName?: string | null;
  note?: string | null;
}

/**
 * La etapa tal como llega del backend. La vista del cliente (mis tickets,
 * referencia+token) trae SOLO `current`/`label`/`enteredAt`/`timeline`: sin
 * `links`, sin `history`, sin quién la movió.
 */
export interface DevStage {
  current: DevStageId;
  label: string;
  enteredAt: number | null;
  /** Fecha en que LLEGÓ el ticket (no se reescribe en cada transición). */
  receivedAt?: number | null;
  timeline: StageStep[];
  resumeStage?: DevStageId | null;
  links?: DevStageLinks;
  history?: DevStageHistoryEntry[];
}

export interface ServiceDeskSettings {
  statuses: StatusOption[];
  /** Configurable lifecycle graph: from-status id → allowed target status ids. */
  transitions?: Record<string, string[]>;
  /** Etapas del ciclo de desarrollo, con su nombre/color/uso por mesa (#559). */
  stages?: StageOption[];
  labels: LabelOption[];
  brands: BrandOption[];
  categories: CategoryOption[];
  priorities: TicketPriority[];
  slaTargets: Record<string, SlaTarget>;
  slaByBrand?: Record<string, Record<string, SlaTarget>>;
  slaRiskThreshold?: number;
  appearance?: Appearance;
  aiAgent?: AiAgentSettings;
  /** Respuesta inmediata con IA (#508) — bloque independiente de `aiAgent`. */
  aiFirstReply?: AiFirstReplySettings;
  /**
   * Canal de WhatsApp por grupos de clientes (#549). `connector: "cloud"` es la
   * API oficial de grupos, que implementa #548: hasta entonces el único
   * conector que funciona es el simulador por fixtures.
   */
  whatsappGroups?: WhatsappGroupsSettings;
  /**
   * Roles que son AGENTES de la mesa además del admin reseller (que siempre lo
   * es). Un agente puede cambiar etiquetas, categoría, prioridad y marca —lo
   * que el agente IA lee en su prompt— y guardar esta configuración; además
   * puede atribuir un ticket a cualquier compañía y listar los usuarios de esa
   * compañía (GET /serviceDesk/company-users).
   * Solo el admin reseller puede modificar la lista (403 en otro caso).
   */
  agentRoleIds?: string[];
  /**
   * Correos que reciben el aviso de SLA incumplido ADEMÁS del agente asignado
   * (función programada `serviceDeskSlaMonitor`). Vacío = sólo el agente.
   */
  slaBreachNotifyEmails?: string[];
  /**
   * Repositorios "owner/repo" que esta mesa puede usar para crear/enlazar
   * issues. Acota `GET /serviceDesk/github/repositories`: el token del servidor
   * suele alcanzar repos ajenos a la mesa y listarlos todos era una fuga de
   * inventario. Vacío = sólo el GITHUB_DEFAULT_REPO del despliegue.
   */
  githubRepos?: string[];
}

/** Fila del selector de repositorios (GET /serviceDesk/github/repositories). */
export interface GithubRepository {
  name: string;
  fullName: string;
  url: string;
  owner: string;
  isPrivate: boolean;
}

/** Configuración del canal de WhatsApp (ver whatsappGroupsService). */
export interface WhatsappGroupsSettings {
  enabled: boolean;
  /**
   * "auto" = manda el despliegue (`SD_WA_CONNECTOR`, y sin ella el simulador).
   * Una elección explícita gana a la variable (#716 · D8).
   */
  connector: "auto" | "simulator" | "cloud";
  /** Un participante sugerido no cuenta hasta que un agente lo confirma. */
  requireContactConfirmation: boolean;
  /** El bot pregunta su nombre y puesto a quien no reconoce. */
  askUnknownParticipants: boolean;
  /**
   * Plantilla APROBADA en Meta con la que se avisa de una respuesta del ticket
   * cuando el grupo está fuera de la ventana de 24 h (#715). `null` = sin
   * plantilla: la respuesta no se publica y queda una nota interna.
   */
  replyTemplate?: WaReplyTemplate | null;
}

/** Plantilla de aviso fuera de la ventana de 24 h (#715). */
export interface WaReplyTemplate {
  /** Nombre en Meta (minúsculas, dígitos y «_»). */
  name: string;
  /** Código de idioma de la plantilla, p. ej. «es_MX». */
  language: string;
  /** La variable {{1}} del cuerpo es la referencia del ticket. */
  includeReference: boolean;
}

export interface PublicConfig {
  brands: BrandOption[];
  categories: CategoryOption[];
  labels: LabelOption[];
  priorities: TicketPriority[];
  appearance?: Appearance;
}

export interface Attachment { name: string; url: string; size?: number | null; contentType?: string | null }

/**
 * Quién escribió un comentario.
 *   customer → el cliente (portal anónimo o "mis tickets")
 *   agent    → una persona del equipo
 *   ai_agent → la respuesta inmediata con IA (#508). Tipo PROPIO a propósito:
 *              nada puede confundir su texto con el de una persona.
 */
export type CommentAuthorType = "customer" | "agent" | "ai_agent";

export interface Comment {
  id: string;
  authorType: CommentAuthorType;
  authorName: string;
  body: string;
  internal?: boolean;
  attachments?: Attachment[];
  createdAt?: TimestampLike;
}

export interface AuditEntry {
  id: string;
  actorName?: string;
  action: string;
  field?: string;
  from?: unknown;
  to?: unknown;
  createdAt?: TimestampLike;
}

export interface SlaStatus {
  overall: SlaState;
  firstResponse?: SlaState;
  resolution?: SlaState;
  paused?: boolean;
  dueAt?: { firstResponse?: number | null; resolution?: number | null };
  // --- CC-CONSOLE #1337: time that is not the team's. Agent view only. -------
  /** Total spent in "Esperando al cliente", including the stretch in progress. */
  waitingCustomerMs?: number;
  /**
   * Resolved → closed. While awaiting confirmation it is the time elapsed since
   * it was resolved; null if it was never resolved.
   */
  closureMs?: number | null;
  confirmationHoldMs?: number;
}

/**
 * Where the ticket stands regarding its closure (CC-CONSOLE #1337). The
 * customer view only carries `pending` and `resolvedAt`; the rest (when it
 * unlocks for the assignee) belongs to the agent view. Dates in epoch ms.
 */
export interface TicketClosure {
  /** In "Resuelto", waiting for whoever opened it to close it. */
  pending: boolean;
  resolvedAt: number | null;
  unlockAt?: number | null;
  unlocked?: boolean;
  lockDays?: number;
}

export interface TicketClosedBy {
  id: string | null;
  name: string | null;
  role: "requester" | "assignee";
}

/** Rework: how many times something not solved was marked as resolved. */
export interface TicketRework {
  count: number;
  // The detail of the last rework only travels in the agent view.
  lastAt?: TimestampLike;
  lastByName?: string | null;
  lastByRole?: "requester" | "staff";
  lastReason?: string | null;
}

/** Resolved tickets the requester has not closed yet; at `limit` or more they cannot open another. */
export interface PendingClosure {
  count: number;
  limit: number;
  blocked: boolean;
  /** `token`: this portal opens a ticket by ref + token, so the owner gets it to follow the link. */
  tickets: { reference: string; token?: string | null; title: string; resolvedAt?: TimestampLike; statusLabel?: string }[];
}

/**
 * Autonomous AI agent bookkeeping. Written only by the machine routes
 * (/serviceDesk/ai/*) that the local Claude runner talks to.
 *   idle → sin trabajar · claimed → el runner lo tiene tomado
 *   planned → triado, con issue de GitHub · pr-open → PR esperando revisión
 *   skipped → no es un problema de código · failed → falló el intento
 */
/** `sandbox`: ticket de ejemplo del Modo Sandbox, fuera de la cola del agente (CC-CONSOLE#1598). */
export type AiAgentState = "idle" | "claimed" | "planned" | "pr-open" | "skipped" | "failed" | "sandbox";

export interface AiAgentInfo {
  state: AiAgentState;
  attempts?: number;
  claimedAt?: TimestampLike;
  leaseExpiresAt?: TimestampLike;
  lastError?: string | null;
  planSummary?: string | null;
  issueUrl?: string | null;
  prUrl?: string | null;
  branch?: string | null;
  repo?: string | null;
  workedAt?: TimestampLike;
  /** Trust gate: staff approved this ticket for the agent (audited). */
  approved?: boolean;
  approvedBy?: string | null;
  approvedByName?: string | null;
  approvedAt?: TimestampLike;
  /**
   * Content frozen at approval time. The runner is served ONLY this (plus its
   * own notes and internal staff notes); changing labels/category/priority
   * afterwards withdraws the approval.
   */
  approvedSnapshot?: {
    title?: string | null;
    description?: string | null;
    category?: string | null;
    labels?: string[];
    priority?: string | null;
    module?: string | null;
    moduleSection?: string | null;
    commentIds?: string[];
  } | null;
  // --- Respuesta inmediata (#508) -------------------------------------------
  /**
   * Sello de idempotencia de la respuesta inmediata: lo escribe el trigger de
   * creación dentro de una transacción, así que una entrega repetida del evento
   * nunca publica una segunda respuesta.
   */
  firstReplyAt?: TimestampLike;
  firstReplyState?: AiFirstReplyState;
  firstReplyCommentId?: string | null;
  firstReplyError?: string | null;
  /** Triage del modelo — sólo para el equipo, el cliente nunca lo ve. */
  firstReplyConfidence?: number | null;
  firstReplyIsCodeIssue?: boolean | null;
  firstReplyNeedsInfo?: boolean | null;
  /**
   * Nivel de acceso con el que se respondió, decidido en el SERVIDOR (#508):
   * `public` = solicitante sin cuenta (o sin compañía) → sólo respuestas no
   * técnicas fundadas en la wiki pública; `company` = autenticado y vinculado.
   */
  firstReplyScope?: AiFirstReplyScope | null;
  /**
   * Por qué se retuvo la redacción del modelo y sólo salió el acuse de recibo.
   * `not_covered` = la wiki pública no cubre la consulta;
   * `technical_content:<familia>` = la redacción traía algo que un solicitante
   * sin cuenta no puede ver.
   */
  firstReplyGuard?: string | null;
  /** Plazo del reclamo: pasado éste, un "working" muerto puede re-reclamarse. */
  firstReplyClaimedUntil?: TimestampLike;
}

/** Nivel de acceso del solicitante al agente de respuesta inmediata (#508). */
export type AiFirstReplyScope = "public" | "company";

/**
 * Bitácora de la respuesta inmediata en el ticket.
 *   idle → aún sin tomar · working → generando · sent → publicada
 *   skipped → no aplicaba · failed → falló (no se reintenta: responde un humano)
 */
export type AiFirstReplyState = "idle" | "working" | "sent" | "skipped" | "failed";

/** Modelos que la mesa puede elegir para la respuesta inmediata. */
export const AI_FIRST_REPLY_MODELS = [
  { id: "claude-haiku-4-5", label: "Haiku 4.5 (económico, recomendado)" },
  { id: "claude-sonnet-5", label: "Sonnet 5 (equilibrado)" },
  { id: "claude-opus-5", label: "Opus 5 (máxima calidad)" },
] as const;

/**
 * Bloque `aiFirstReply` de los ajustes: la RESPUESTA INMEDIATA con IA (#508).
 * Es independiente de `aiAgent` (el agente de código): encender uno nunca
 * enciende el otro. Éste sí le escribe al cliente; aquél nunca lo hace.
 */
export interface AiFirstReplySettings {
  enabled: boolean;
  /** Persona / instrucciones editables de esta mesa. */
  persona: string;
  /** Nombre con el que se firma la respuesta en el hilo y en el correo. */
  signature: string;
  model: string;
  maxTokens: number;
  timeoutMs: number;
  /** Marcas "sin IA": un ticket con cualquiera de éstas nunca recibe respuesta. */
  excludedLabels: string[];
  excludedCategories: string[];
  excludedBrands: string[];
  /**
   * Conversación continua (#509): el agente también responde los comentarios
   * públicos del CLIENTE en el hilo. Aparte de `enabled`: encender la primera
   * respuesta no enciende la conversación.
   */
  conversationEnabled: boolean;
  /** Tope de turnos del agente por ticket (1–20). Al llegar, traspasa a una persona. */
  conversationMaxTurns: number;
  /** Tope de costo por ticket, en USD (0.01–20). Mismo efecto que el de turnos. */
  conversationMaxCostUsd: number;
}

/**
 * Bitácora de la conversación continua (#509) en el ticket. La escribe SÓLO el
 * servidor y vive fuera de `aiAgent`: nada de esto toca la aprobación ni el
 * snapshot del agente de código.
 */
export interface AiConversationInfo {
  // `human_owns`: una persona del equipo contestó en el hilo y el agente se
  // retiró; «Reactivar» se lo devuelve (sella `resumedAt`).
  state?: "idle" | "working" | "handed_off" | "failed" | "skipped" | "handoff" | "human_owns";
  turns?: number;
  costUsd?: number;
  handedOffAt?: TimestampLike;
  handoffReason?: AiConversationHandoffReason | null;
  pausedByBot?: boolean;
  pausedFromStatus?: string | null;
  resumedAt?: TimestampLike;
}

export type AiConversationHandoffReason =
  | "customer_requested_human"
  | "customer_frustrated"
  | "cap_turns"
  | "cap_cost"
  | "not_covered"
  | "technical_content"
  | "unsafe_content";

export const AI_CONVERSATION_HANDOFF_LABEL: Record<AiConversationHandoffReason, string> = {
  customer_requested_human: "El cliente pidió hablar con una persona",
  customer_frustrated: "El cliente se notaba frustrado",
  cap_turns: "Se alcanzó el tope de turnos",
  cap_cost: "Se alcanzó el tope de costo",
  not_covered: "Sin cuenta y sin respuesta en la wiki pública",
  technical_content: "Se retuvo una respuesta con contenido técnico",
  unsafe_content: "Se retuvo una respuesta con datos que no se comparten",
};

/** Editable `aiAgent` block of the Service Desk settings document. */
export interface AiAgentSettings {
  enabled: boolean;
  /**
   * When true (default) a ticket only reaches the agent after staff approval.
   * Tickets from the anonymous portal always need it, whatever this says.
   */
  requireApproval: boolean;
  maxAttempts: number;
  leaseMinutes: number;
  eligibleLabels: string[];
  eligibleCategories: string[];
  minPriority: TicketPriority | null;
}

export interface Ticket {
  id: string;
  reference: string;
  token?: string; // present in my-tickets + public create response
  brand: string;
  brandLabel?: string;
  title: string;
  description?: string;
  category?: string;
  module?: string;
  moduleSection?: string;
  priority: TicketPriority;
  status: string;
  statusLabel?: string;
  labels?: string[];
  requester?: { name: string | null; email: string | null; userId?: string | null };
  assignedAgentId?: string | null;
  /**
   * Nombre del agente asignado guardado en el ticket. Lo traen los tickets de
   * ejemplo del Sandbox (sus agentes no son cuentas reales); la interfaz lo usa
   * cuando el id no está entre los usuarios asignables.
   */
  assignedAgentName?: string | null;
  assignedDevId?: string | null;
  /** Ticket de ejemplo del Modo Sandbox (sólo lo ve su compañía; CC-CONSOLE#1598). */
  sandbox?: boolean;
  demoSeed?: boolean;
  /** Canal por el que llegó: whatsapp | email | portal | phone. */
  channel?: string | null;
  /** Empresa del solicitante, guardada en el ticket (Sandbox). */
  companyName?: string | null;
  escalated?: boolean;
  /**
   * Marca "sin IA" de ESTE ticket (#508): apaga la respuesta inmediata para un
   * caso puntual sin tocar la configuración de la mesa. Sólo la escribe un
   * agente, vía `PUT /serviceDesk/tickets/:id`.
   */
  aiFirstReplyDisabled?: boolean;
  /** Conversación continua con IA (#509). Sólo en la vista de agente. */
  aiConversation?: AiConversationInfo | null;
  /** "public" (portal anónimo) | "authenticated". */
  source?: string;
  /** Solo en la vista de agente: las vistas de cliente (mis tickets, ref+token) no exponen ids internos. */
  companyId?: string | null;
  /**
   * Agent view: full snapshot. Customer/public views (mis tickets, ref+token)
   * only get { name, roleName } — id/email/roleId are stripped by the backend.
   */
  affectedUser?: (Partial<AffectedUser> & { name: string | null; roleName: string | null }) | null;
  githubIssue?: { number?: number; htmlUrl?: string; url?: string; state?: string; repo?: string } | null;
  aiAgent?: AiAgentInfo | null;
  /** Puente a ingeniería (#510): estado del issue de GitHub automático. Sólo vista de agente. */
  engineeringBridge?: EngineeringBridgeInfo | null;
  /**
   * Etapa del ciclo de desarrollo (#559). En la vista de agente incluye los
   * enlaces y la bitácora; en las vistas de cliente, sólo etapa y fechas.
   */
  devStage?: DevStage | null;
  sla?: { overall: SlaState; paused?: boolean };
  slaStatus?: SlaStatus;
  /** Requester-driven closure state (CC-CONSOLE #1337). */
  closure?: TicketClosure | null;
  /** Who closed it and in what capacity. Agent view only. */
  closedBy?: TicketClosedBy | null;
  rework?: TicketRework | null;
  slaBreachNotifiedDate?: string | null;
  firstResponseAt?: TimestampLike;
  resolvedAt?: TimestampLike;
  closedAt?: TimestampLike;
  createdAt?: TimestampLike;
  updatedAt?: TimestampLike;
}

export interface AssignableUser { id: string; name: string; email: string | null }

/**
 * Usuario que sufre el problema — no siempre es quien abre el ticket.
 * En GET /serviceDesk/company-users un no-agente sólo recibe { id, name, roleName }:
 * `email` y `roleId` llegan como null (normalizeCompanyUsers los rellena).
 */
export interface AffectedUser {
  id: string;
  name: string;
  email: string | null;
  roleId: string | null;
  roleName: string | null;
}

export interface CompanyUsersResult {
  /**
   * Veredicto del BACKEND sobre si quien llama es agente de la mesa (admin
   * reseller o un rol de `settings.agentRoleIds`). El formulario de alta lo usa
   * para decidir si ofrece el selector de compañía, en lugar de deducirlo de
   * localStorage.userRoleId. Es sólo comodidad de UI: el 403 del alta manda.
   */
  isAgent: boolean;
  /** Compañía que resolvió el backend (para no agentes: SIEMPRE users.company_id). */
  companyId: string | null;
  companyName: string | null;
  users: AffectedUser[];
}

/**
 * Usuarios de una compañía, para el desplegable de «usuario afectado».
 * Sin `companyId` el backend responde con la compañía del usuario en sesión
 * (users.company_id) — la fuente de verdad, no localStorage. Pedir otra
 * compañía sólo lo puede un agente de la mesa de servicio (si no, 403).
 */
export async function getCompanyUsers(companyId?: string): Promise<CompanyUsersResult> {
  const res = await api.get("/serviceDesk/company-users", companyId ? { params: { companyId } } : undefined);
  return normalizeCompanyUsers(unwrap(res), companyId);
}

// Fills the agent-only fields (email/roleId) with null for non-agent rows.
function normalizeAffectedUser(u: Partial<AffectedUser> & { id: string }): AffectedUser {
  return {
    id: u.id,
    name: u.name ?? u.id,
    email: u.email ?? null,
    roleId: u.roleId ?? null,
    roleName: u.roleName ?? null,
  };
}

// Tolerates the legacy bare-array shape and malformed payloads.
export function normalizeCompanyUsers(payload: unknown, requestedCompanyId?: string): CompanyUsersResult {
  const rows = (list: unknown): AffectedUser[] =>
    Array.isArray(list)
      ? (list as Partial<AffectedUser>[])
          .filter((u): u is Partial<AffectedUser> & { id: string } => Boolean(u && typeof u.id === "string"))
          .map(normalizeAffectedUser)
      : [];
  if (Array.isArray(payload)) {
    // Forma legacy (array pelado): sin veredicto del servidor → no agente.
    return { isAgent: false, companyId: requestedCompanyId || null, companyName: null, users: rows(payload) };
  }
  const p = (payload && typeof payload === "object" ? payload : {}) as Partial<CompanyUsersResult>;
  return {
    // Falla cerrado: cualquier cosa que no sea `true` es "no agente".
    isAgent: p.isAgent === true,
    companyId: typeof p.companyId === "string" ? p.companyId : requestedCompanyId || null,
    companyName: typeof p.companyName === "string" ? p.companyName : null,
    users: rows(p.users),
  };
}

export interface Metrics {
  /** Vista de ejemplo activa (CC-CONSOLE#1598) o null. */
  sandboxView?: SandboxView | null;
  totals: { all: number; open: number; closed: number };
  volumeByDay: { day: string; count: number }[];
  avgFirstResponseMins: number | null;
  avgResolutionMins: number | null;
  byCategory: { key: string; count: number }[];
  byLabel: { key: string; count: number }[];
  byBrand: { key: string; label: string; count: number }[];
  byPriority: { key: string; count: number }[];
  slaCompliancePct: number | null;
  slaCompliant: number;
  slaBreached: number;
  /**
   * Muestras DESCARTADAS de los promedios por tener el fin antes del inicio
   * (datos importados / relojes desfasados, #410). Si es > 0 hay tickets con
   * sellos incoherentes que conviene revisar — antes se colaban en la media y
   * la hacían negativa.
   */
  skewedSamples?: number;
  // CC-CONSOLE #1337 — optional: an older backend (deployed dev vs. emulator)
  // does not send them and the dashboard must not render "undefined".
  avgWaitingCustomerMins?: number | null;
  avgRequesterClosureMins?: number | null;
  pendingClosure?: number;
  pendingClosureUnlocked?: number;
  reworkedTickets?: number;
  reworkTotal?: number;
}

// ---------------------------------------------------------------------------
// PUBLIC (anonymous portal)
// ---------------------------------------------------------------------------
export async function getPublicConfig(): Promise<PublicConfig> {
  const res = await publicApi.get("/serviceDesk/public/config");
  return unwrap(res);
}

export interface CreatePublicTicketInput {
  name?: string;
  email: string;
  brand: string;
  title: string;
  description?: string;
  category?: string;
  module?: string;
  moduleSection?: string;
  priority?: TicketPriority;
  /** Ids de etiqueta (settings.labels). Viajan como JSON: el parser multipart los convierte en arreglo. */
  labels?: string[];
  files?: File[];
  companyId?: string;
  deviceImeis?: string[];
}

/**
 * Alta autenticada. En WAYAK la compañía y el usuario afectado son OPCIONALES
 * en el cliente: el portal, «Mis tickets» y el diálogo de Ayuda no los piden, y
 * cuando faltan el BACKEND los deriva de la sesión (la compañía de quien llama
 * y, como afectado, quien llama). Si se mandan, el backend los valida igual:
 * quien no es agente queda fijado a su users.company_id (otra compañía = 403) y
 * el usuario afectado debe pertenecer a la compañía del ticket.
 */
export interface CreateMyTicketInput extends Omit<CreatePublicTicketInput, "email"> {
  email?: string;
  /** Opcional: si falta, la compañía de la sesión. Nunca se confía en este valor. */
  companyId?: string;
  /** Opcional: usuario de esa compañía al que le ocurre el problema; si falta, quien llama. */
  affectedUserId?: string;
}

export interface CreateTicketResult {
  reference: string;
  token: string;
  statusUrl: string;
  ticket: Ticket;
}

export async function createPublicTicket(input: CreatePublicTicketInput): Promise<CreateTicketResult> {
  const form = new FormData();
  form.append("name", input.name || "");
  form.append("email", input.email);
  form.append("brand", input.brand);
  form.append("title", input.title);
  form.append("description", input.description || "");
  if (input.category) form.append("category", input.category);
  if (input.module) form.append("module", input.module);
  if (input.moduleSection) form.append("moduleSection", input.moduleSection);
  if (input.priority) form.append("priority", input.priority);
  if (input.companyId) form.append("companyId", input.companyId);
  if (input.deviceImeis && input.deviceImeis.length > 0) {
    input.deviceImeis.forEach((imei, i) => form.append(`deviceImeis[${i}]`, imei));
  }
  (input.files || []).forEach((f, i) => form.append(`attachment${i}`, f));
  const res = await publicApi.post("/serviceDesk/public/tickets", form);
  return unwrap(res);
}

export async function getPublicTicket(ref: string, token: string): Promise<{ ticket: Ticket; comments: Comment[] }> {
  const res = await publicApi.get("/serviceDesk/public/tickets", { params: { ref, token } });
  return unwrap(res);
}

export async function addPublicComment(
  ref: string,
  token: string,
  body: string,
  files: File[] = [],
): Promise<Comment> {
  const form = new FormData();
  form.append("body", body);
  files.forEach((f, i) => form.append(`attachment${i}`, f));
  const res = await publicApi.post("/serviceDesk/public/tickets/comments", form, {
    params: { ref, token },
  });
  return unwrap(res);
}

// ---------------------------------------------------------------------------
// AUTHED — customer
// ---------------------------------------------------------------------------
export async function createMyTicket(input: CreateMyTicketInput): Promise<Ticket> {
  const form = new FormData();
  form.append("brand", input.brand);
  form.append("title", input.title);
  form.append("description", input.description || "");
  if (input.category) form.append("category", input.category);
  if (input.module) form.append("module", input.module);
  if (input.moduleSection) form.append("moduleSection", input.moduleSection);
  if (input.priority) form.append("priority", input.priority);
  if (input.labels && input.labels.length > 0) form.append("labels", JSON.stringify(input.labels));
  if (input.email) form.append("requesterEmail", input.email);
  if (input.name) form.append("requesterName", input.name);
  if (input.companyId) form.append("companyId", input.companyId);
  if (input.affectedUserId) form.append("affectedUserId", input.affectedUserId);
  (input.files || []).forEach((f, i) => form.append(`attachment${i}`, f));
  const res = await apiFormData.post("/serviceDesk/tickets", form);
  return unwrap(res);
}

export async function getMyTickets(): Promise<Ticket[]> {
  const res = await api.get("/serviceDesk/tickets/mine");
  const data = unwrap(res);
  // #451: never hand a non-array to a .map() — an unexpected shape is an error.
  if (!Array.isArray(data)) throw new Error("Respuesta inesperada al cargar tus tickets");
  return data;
}

/**
 * Tickets in "Resuelto" the user opened and has not closed (CC-CONSOLE #1337).
 * When `blocked` the backend rejects creation (409 SD_PENDING_CLOSURE_LIMIT),
 * so the forms ask first instead of letting the user fill in something they
 * will not be able to send.
 */
export async function getPendingClosure(): Promise<PendingClosure> {
  const res = await api.get("/serviceDesk/tickets/pending-closure");
  const data = unwrap(res) || {};
  return {
    count: Number(data.count) || 0,
    limit: Number(data.limit) || 0,
    blocked: data.blocked === true,
    tickets: Array.isArray(data.tickets) ? data.tickets : [],
  };
}

/**
 * The requester closes their own ticket: the confirmation that it was solved.
 * Authenticated on purpose (not `publicApi`): the ref + token link can be
 * forwarded, and closing belongs to the person who opened the ticket.
 */
export async function closeMyTicket(ref: string): Promise<Ticket> {
  const res = await api.post("/serviceDesk/tickets/public/close", undefined, { params: { ref } });
  return unwrap(res);
}

/**
 * The requester says it was NOT solved: the ticket goes from "Resuelto" to
 * "Rehacer" and adds to the rework counter. The backend requires the reason
 * (5 characters minimum) and only accepts it while the ticket is in "Resuelto".
 */
export async function requestTicketRework(ref: string, reason: string): Promise<Ticket> {
  const res = await api.post("/serviceDesk/tickets/public/rework", { reason }, { params: { ref } });
  return unwrap(res);
}

// ---------------------------------------------------------------------------
// AUTHED — agent
// ---------------------------------------------------------------------------
export interface ListFilters {
  scope?: "open" | "closed" | "all";
  brand?: string;
  status?: string;
  priority?: string;
  label?: string;
  agentId?: string;
  sla?: SlaState;
  q?: string;
  page?: number;
  limit?: number;
}

export async function listTickets(filters: ListFilters = {}): Promise<{ data: Ticket[]; totalItems: number; totalPages: number; currentPage: number }> {
  const res = await api.get("/serviceDesk/tickets", { params: filters });
  const body = res.data ?? {};
  if (useSampleViewStore.getState().active) useSampleViewStore.getState().confirm(readSandboxView(body.sandboxView));
  return {
    data: Array.isArray(body.data) ? body.data : [],
    totalItems: body.meta?.totalItems ?? 0,
    totalPages: body.meta?.totalPages ?? 1,
    currentPage: body.meta?.currentPage ?? 1,
  };
}

export async function getTicket(id: string): Promise<{ ticket: Ticket; comments: Comment[]; audit: AuditEntry[]; attachments?: Attachment[] }> {
  const res = await api.get(`/serviceDesk/tickets/${id}`);
  return unwrap(res);
}

export async function updateTicket(id: string, patch: Partial<{
  status: string;
  priority: TicketPriority;
  labels: string[];
  category: string;
  module: string;
  moduleSection: string;
  brand: string;
  assignedAgentId: string | null;
  assignedDevId: string | null;
  escalated: boolean;
  companyId: string | null;
  affectedUserId: string | null;
  /** Marca "sin IA" de este ticket (#508). Sólo un agente puede cambiarla. */
  aiFirstReplyDisabled: boolean;
  /** Reactiva la conversación con IA tras un traspaso (#509). Sólo agentes. */
  aiConversationResume: true;
}>): Promise<Ticket> {
  const res = await api.put(`/serviceDesk/tickets/${id}`, patch);
  return unwrap(res);
}

/** Elimina el ticket y sus adjuntos. Sólo el admin reseller (403 en otro caso). */
export async function deleteTicket(id: string): Promise<void> {
  await api.delete(`/serviceDesk/tickets/${id}`);
}

export async function addAgentComment(id: string, body: string, internal: boolean, files: File[] = []): Promise<Comment> {
  const form = new FormData();
  form.append("body", body);
  form.append("internal", String(internal));
  files.forEach((f, i) => form.append(`attachment${i}`, f));
  const res = await apiFormData.post(`/serviceDesk/tickets/${id}/comments`, form);
  return unwrap(res);
}

export async function linkGithubIssue(id: string, input: { repo?: string; title?: string; body?: string; labels?: string[] }): Promise<Ticket> {
  const res = await api.post(`/serviceDesk/tickets/${id}/github`, input);
  return unwrap(res);
}

/** Link an EXISTING GitHub issue by pasting its URL (or "owner/repo#123"). */
export async function linkExistingGithubIssue(id: string, url: string): Promise<Ticket> {
  const res = await api.post(`/serviceDesk/tickets/${id}/github/link`, { url });
  return unwrap(res);
}

/** Lo que devuelven los controles humanos del agente: solo lo que el panel refresca. */
export interface AiAgentPanelResult {
  id: string;
  status: string;
  aiAgent: AiAgentInfo;
  engineeringBridge?: EngineeringBridgeInfo | null;
}

// ---------------------------------------------------------------------------
// Puente a ingeniería (#510)
// ---------------------------------------------------------------------------
export type EngineeringBridgeState = "pending" | "creating" | "linked" | "failed";

export interface EngineeringBridgeInfo {
  state: EngineeringBridgeState;
  repo?: string | null;
  /** github_not_configured | repo_not_resolved | github_error | create_uncertain | verify_failed | repo_not_allowed | … */
  reason?: string | null;
  message?: string | null;
  labelled?: boolean;
  attempts?: number;
}

export const ENGINEERING_BRIDGE_META: Record<EngineeringBridgeState, { label: string; color: string; bg: string }> = {
  pending: { label: "Pendiente", color: "#b45309", bg: "#fef3c7" },
  creating: { label: "Creando issue…", color: "#2563eb", bg: "#dbeafe" },
  linked: { label: "Issue vinculado", color: "#059669", bg: "#d1fae5" },
  failed: { label: "Falló", color: "#dc2626", bg: "#fee2e2" },
};

/** ¿Mostrar «Reintentar puente»? Sólo cuando no hay issue y nada está en curso. */
export function canRetryEngineeringBridge(bridge: EngineeringBridgeInfo | null | undefined): boolean {
  return bridge?.state === "pending" || bridge?.state === "failed";
}

/** ¿El puente necesita que el staff elija el repositorio? */
export function engineeringBridgeNeedsRepo(bridge: EngineeringBridgeInfo | null | undefined): boolean {
  return bridge?.reason === "repo_not_resolved";
}

/**
 * Reintenta el puente a ingeniería de un ticket aprobado (solo staff). `repo`
 * tiene que ser uno de la lista blanca de la mesa; el backend lo comprueba.
 */
export async function retryEngineeringBridge(id: string, repo?: string): Promise<AiAgentPanelResult> {
  const res = await api.post(`/serviceDesk/tickets/${id}/ai/bridge`, repo ? { repo } : {});
  return unwrap(res);
}

/**
 * "Reintentar con el agente IA" (solo staff). El backend devuelve el ticket a
 * Abierto si hace falta, reinicia los intentos y lo pone en la cola. `force`
 * libera además un lease vigente (nunca lo manda el botón).
 */
export async function releaseAiAgent(id: string, force = false): Promise<AiAgentPanelResult> {
  const res = await api.post(`/serviceDesk/tickets/${id}/ai/release`, force ? { force: true } : {});
  return unwrap(res);
}

/** Aprueba el ticket para el agente IA (puerta de confianza, solo staff). */
export async function approveAiAgent(id: string): Promise<AiAgentPanelResult> {
  const res = await api.post(`/serviceDesk/tickets/${id}/ai/approve`, {});
  return unwrap(res);
}

/**
 * ¿Quien mira es staff del agente IA? Veredicto del MISMO guardia del backend
 * que protege aprobar / retirar / reintentar / reactivar (ids, compañía+rol o
 * dominio de correo VERIFICADO). Sólo decide qué botones se pintan: cada acción
 * se vuelve a validar en el servidor. Cualquier cosa que no sea `true` es no.
 */
export async function getAiStaffStatus(): Promise<boolean> {
  return (await getAiStaffStatusDetail()).isAiStaff;
}

/**
 * Igual que `getAiStaffStatus`, más `needsGoogleProof`: la persona es del
 * dominio de staff pero su cuenta aún no tiene la prueba de identidad con
 * Google (PR #706). Entonces la ficha ofrece «Verificar con Google»
 * (services/googleIdentityProof). Sólo un `true` literal cuenta.
 */
export async function getAiStaffStatusDetail(): Promise<{ isAiStaff: boolean; needsGoogleProof: boolean }> {
  const res = await api.get("/serviceDesk/staff/me");
  const data = unwrap(res);
  return { isAiStaff: data?.isAiStaff === true, needsGoogleProof: data?.needsGoogleProof === true };
}

/** Retira la aprobación: el agente no lo toma y una corrida viva se detiene. */
export async function revokeAiAgentApproval(id: string): Promise<AiAgentPanelResult> {
  const res = await api.post(`/serviceDesk/tickets/${id}/ai/revoke`, {});
  return unwrap(res);
}

// ---------------------------------------------------------------------------
// Etapas del ciclo de desarrollo (#559)
// ---------------------------------------------------------------------------

/**
 * Mueve la etapa del ticket. Acción de AGENTE de la mesa: la línea de tiempo la
 * ve el cliente. A diferencia de las transiciones automáticas, el staff SÍ
 * puede retroceder (corregir lo que una máquina dejó mal).
 */
export async function setTicketStage(
  id: string,
  stage: DevStageId,
  input: { note?: string; links?: DevStageLinks } = {},
): Promise<{ id: string; devStage: DevStage }> {
  const res = await api.post(`/serviceDesk/tickets/${id}/stage`, { stage, ...input });
  return unwrap(res);
}

// ---------------------------------------------------------------------------
// Agentes de código — registro y presencia (#511)
// ---------------------------------------------------------------------------
export type AgentPresence = "online" | "working" | "offline" | "revoked";

export interface ServiceDeskAgent {
  id: string;
  name: string;
  host: string | null;
  version: string | null;
  /** Primeros caracteres de la llave, sólo para distinguir dos agentes. */
  keyPrefix: string | null;
  lastSeenAt: number | null;
  revokedAt: number | null;
  createdAt: number | null;
  createdByName?: string | null;
  /**
   * El registro es de la MESA, no por marca: a un operador acotado el backend
   * le entrega el trabajo en curso sin la referencia del ticket.
   */
  currentJob?: { ticketId: string | null; reference?: string | null; since?: unknown } | null;
  presence: AgentPresence;
}

export interface AgentsPanel {
  agents: ServiceDeskAgent[];
  presence: { online: number; working: number; offline: number; revoked: number; total: number; live: number };
  /**
   * `waiting` es un SUELO: el barrido está acotado, así que `capped` indica que
   * hay al menos esos. `unavailable` = el barrido falló y el panel se sirvió
   * igual (la lista de agentes y el botón de revocar no dependen de él).
   */
  queue: {
    enabled: boolean;
    waiting: number;
    oldestWaitingAt: number | null;
    capped?: boolean;
    unavailable?: boolean;
  };
  /**
   * Aviso cuando hay cola de código y CERO agentes conectados. `level: "info"`
   * cuando la mesa sólo tiene la llave global heredada (que no reporta
   * presencia): ahí «nadie en línea» no significa «nadie trabajando».
   */
  warning: { code: string; message: string; level?: "info" | "error" } | null;
}

export async function getServiceDeskAgents(): Promise<AgentsPanel> {
  const res = await api.get("/serviceDesk/agents");
  return unwrap(res);
}

/**
 * Da de alta un agente. La llave en claro viene en `key` y es la ÚNICA vez que
 * existe fuera del runner: el backend sólo guarda su sha256.
 */
export async function createServiceDeskAgent(input: {
  name: string;
  host?: string;
  version?: string;
}): Promise<{ agent: ServiceDeskAgent; key: string; keyHint?: string }> {
  const res = await api.post("/serviceDesk/agents", input);
  return unwrap(res);
}

export async function renameServiceDeskAgent(id: string, name: string): Promise<ServiceDeskAgent> {
  const res = await api.put(`/serviceDesk/agents/${id}`, { name });
  return unwrap(res);
}

/** Revocar es inmediato: la siguiente llamada del runner con esa llave es 401. */
export async function revokeServiceDeskAgent(id: string): Promise<ServiceDeskAgent> {
  const res = await api.post(`/serviceDesk/agents/${id}/revoke`, {});
  return unwrap(res);
}

export async function getSettings(): Promise<ServiceDeskSettings> {
  const res = await api.get("/serviceDesk/settings");
  return unwrap(res);
}

export async function updateSettings(patch: Partial<ServiceDeskSettings>): Promise<ServiceDeskSettings> {
  const res = await api.put("/serviceDesk/settings", patch);
  return unwrap(res);
}

export async function getAssignableUsers(): Promise<AssignableUser[]> {
  const res = await api.get("/serviceDesk/assignable-users");
  return unwrap(res) || [];
}

/**
 * Repositorios que la mesa tiene configurados para GitHub. El servidor sólo
 * devuelve los de `settings.githubRepos` (+ GITHUB_DEFAULT_REPO), nunca todos
 * los que alcanza su token.
 */
export async function listGithubRepositories(): Promise<GithubRepository[]> {
  const res = await api.get("/serviceDesk/github/repositories");
  return unwrap(res) || [];
}

export async function getMetrics(params: { brand?: string; from?: string; to?: string } = {}): Promise<Metrics> {
  const res = await api.get("/serviceDesk/metrics", { params });
  const metrics = unwrap(res) as Metrics;
  if (useSampleViewStore.getState().active) useSampleViewStore.getState().confirm(readSandboxView(metrics?.sandboxView));
  return metrics;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
export function tsToDate(value: TimestampLike | undefined): Date | null {
  if (!value) return null;
  if (typeof value === "number") return new Date(value);
  if (typeof value === "string") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  const seconds = value._seconds ?? value.seconds;
  if (typeof seconds === "number") return new Date(seconds * 1000);
  return null;
}

/**
 * ¿Mostrar "Reintentar con el agente IA"? Estados terminales del intento
 * (falló, no aplica, PR abierto) o una corrida interrumpida cuyo lease ya
 * venció. Con el lease vivo el backend respondería 409: el runner sigue
 * trabajando y reintentar duplicaría el trabajo.
 */
export function canRetryAiAgent(ai: AiAgentInfo | null | undefined, now = Date.now()): boolean {
  if (!ai) return false;
  if (["failed", "skipped", "pr-open"].includes(ai.state)) return true;
  if (ai.state === "claimed" || ai.state === "planned") {
    const exp = tsToDate(ai.leaseExpiresAt);
    return !exp || exp.getTime() <= now;
  }
  return false;
}

/**
 * Aprobación vigente, igual que isApproved del backend: bandera + fecha + el
 * snapshot del contenido aprobado (una aprobación sin snapshot no cuenta).
 */
export function isAiAgentApproved(ai: AiAgentInfo | null | undefined): boolean {
  return Boolean(ai?.approved && ai.approvedAt && ai.approvedSnapshot);
}

/** Espejo de la puerta de confianza del backend (eligibilityReason). */
export function aiAgentNeedsApproval(
  ticket: Pick<Ticket, "source" | "aiAgent">,
  settings?: Pick<ServiceDeskSettings, "aiAgent"> | null,
): boolean {
  if (isAiAgentApproved(ticket.aiAgent)) return false;
  return settings?.aiAgent?.requireApproval !== false || ticket.source !== "authenticated";
}

export function formatDate(value: TimestampLike | undefined): string {
  const d = tsToDate(value);
  if (!d) return "—";
  return d.toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
}

export const SLA_META: Record<SlaState, { label: string; color: string; bg: string }> = {
  met: { label: "Cumplido", color: "#059669", bg: "#d1fae5" },
  on_time: { label: "En tiempo", color: "#059669", bg: "#d1fae5" },
  at_risk: { label: "En riesgo", color: "#b45309", bg: "#fef3c7" },
  breached: { label: "Incumplido", color: "#dc2626", bg: "#fee2e2" },
  unknown: { label: "—", color: "#6b7280", bg: "#f3f4f6" },
};

export const AI_AGENT_META: Record<AiAgentState, { label: string; color: string; bg: string }> = {
  idle: { label: "Sin trabajar", color: "#6b7280", bg: "#f3f4f6" },
  claimed: { label: "Trabajando", color: "#7c3aed", bg: "#ede9fe" },
  planned: { label: "Planificado", color: "#2563eb", bg: "#dbeafe" },
  "pr-open": { label: "PR abierto", color: "#059669", bg: "#d1fae5" },
  skipped: { label: "No aplica", color: "#6b7280", bg: "#f3f4f6" },
  failed: { label: "Falló", color: "#dc2626", bg: "#fee2e2" },
  sandbox: { label: "Ejemplo (Sandbox)", color: "#6b7280", bg: "#f3f4f6" },
};

/** Debe coincidir con DEFAULT_AI_FIRST_REPLY del backend (serviceDeskConstants.js). */
export const DEFAULT_AI_FIRST_REPLY: AiFirstReplySettings = {
  enabled: false,
  persona: "",
  signature: "Agente IA",
  model: "claude-haiku-4-5",
  maxTokens: 700,
  timeoutMs: 20000,
  excludedLabels: [],
  excludedCategories: [],
  excludedBrands: [],
  conversationEnabled: false,
  conversationMaxTurns: 5,
  conversationMaxCostUsd: 0.5,
};

export const AI_FIRST_REPLY_META: Record<AiFirstReplyState, { label: string; color: string; bg: string }> = {
  idle: { label: "Sin responder", color: "#6b7280", bg: "#f3f4f6" },
  working: { label: "Redactando", color: "#7c3aed", bg: "#ede9fe" },
  sent: { label: "Respondido", color: "#059669", bg: "#d1fae5" },
  skipped: { label: "No aplicaba", color: "#6b7280", bg: "#f3f4f6" },
  failed: { label: "Falló", color: "#dc2626", bg: "#fee2e2" },
};

/**
 * Espejo de DEV_STAGES del backend (serviceDeskConstants.js). Sólo se usa como
 * respaldo cuando no hay ajustes cargados: el nombre y el color REALES vienen
 * siempre del backend, que ya aplicó la configuración de la mesa.
 */
export const DEV_STAGE_META: Record<DevStageId, { label: string; color: string; kind: "linear" | "side" }> = {
  received: { label: "Recibido", color: "#64748B", kind: "linear" },
  analysis: { label: "En análisis", color: "#3B82F6", kind: "linear" },
  development: { label: "En desarrollo", color: "#8B5CF6", kind: "linear" },
  pr_review: { label: "PR en revisión", color: "#0EA5E9", kind: "linear" },
  qa_deploy: { label: "Desplegado en QA", color: "#14B8A6", kind: "linear" },
  staff_validated: { label: "Validado por staff", color: "#22C55E", kind: "linear" },
  resolved: { label: "Resuelto", color: "#10B981", kind: "linear" },
  waiting_customer: { label: "Esperando cliente", color: "#F59E0B", kind: "side" },
  qa_rejected: { label: "Rechazado en QA", color: "#EF4444", kind: "side" },
};

export const DEV_STAGE_ORDER: DevStageId[] = [
  "received",
  "analysis",
  "development",
  "pr_review",
  "qa_deploy",
  "staff_validated",
  "resolved",
  "waiting_customer",
  "qa_rejected",
];

/** Nombre configurado por la mesa, con el de fábrica como respaldo. */
export function stageLabelOf(settings: ServiceDeskSettings | null | undefined, id: DevStageId): string {
  const configured = settings?.stages?.find((s) => s.id === id);
  return configured?.label || DEV_STAGE_META[id]?.label || id;
}

/** Etapas que el staff puede elegir: las que la mesa tiene activas. */
export function selectableStages(settings: ServiceDeskSettings | null | undefined): StageOption[] {
  const list = settings?.stages?.length
    ? settings.stages
    : DEV_STAGE_ORDER.map((id) => ({ id, ...DEV_STAGE_META[id], enabled: true }));
  return list.filter((s) => s.enabled !== false);
}

/** ¿Hay algún enlace técnico que enseñar en la vista interna? */
export function hasStageLinks(links: DevStageLinks | undefined | null): boolean {
  if (!links) return false;
  return Boolean(links.issueUrl || links.prUrl || links.qaUrl || links.branch || links.repo);
}

export const PRIORITY_META: Record<TicketPriority, { label: string; color: string }> = {
  low: { label: "Baja", color: "#6b7280" },
  medium: { label: "Media", color: "#2563eb" },
  high: { label: "Alta", color: "#d97706" },
  urgent: { label: "Urgente", color: "#dc2626" },
};

// Status ids reachable from `current` per the configured transition graph,
// always including the current status itself (a no-op move). When no graph is
// configured every status is selectable.
export function allowedNextStatuses(settings: ServiceDeskSettings | null, current: string): string[] {
  const all = (settings?.statuses || []).map((s) => s.id);
  const graph = settings?.transitions;
  if (!graph || !graph[current]) return all;
  return all.filter((id) => id === current || graph[current].includes(id));
}

export function formatMins(mins: number | null): string {
  if (mins == null) return "—";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return m ? `${h} h ${m} min` : `${h} h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh ? `${d} d ${rh} h` : `${d} d`;
}
